import React, { useMemo } from 'react';
import { ArrowDownTrayIcon, ArrowLeftIcon, CircleStackIcon, TableCellsIcon } from '@heroicons/react/20/solid';
import ButtonLinkList from '@ui/button/ButtonLinkList';
import ChartCard, { ChartCardState } from '@ui/chart/ChartCard';
import DataTable, { DataTableRow, DataTableSort } from '@ui/chart/DataTable';
import KpiStrip from '@ui/chart/KpiStrip';
import BarChart from '@ui/chart/BarChart';
import LineChart from '@ui/chart/LineChart';
import { barChartTable, lineChartTable } from '@ui/chart/chartTable';
import ButtonTabs from '@ui/tabs/ButtonTabs';
import CheckboxMenu from '@ui/menu/CheckboxMenu';
import ToolbarButton from '@ui/button/ToolbarButton';
import { MonthRange } from '@ui/selector/MonthRangePicker';
import { ChannelEvolution, RevenueScope, VatBasis } from '@app/model/bi/Bi';
import { formatDate, missingNote, missingOf, revenueContent, revenueName } from '../Bi.utilities';
import { nightsLabel, periodLabel, WINDOWS } from './RatePlanCards';
import { focusFigures, OTHER_LINE, pointsOf } from './RatePlans.utilities';
import {
	CHANNEL_BLOCKS, ChannelBlock, ChannelLine, ChannelNode, ChannelTree, columnsOf, EvolutionMeasure, evolutionByRow, evolutionLines, MixComparison, MixMeasure, mixOf,
	ROW_LIMITS, tableRows, totalRow,
} from './Channels.utilities';

// The cards of the Channels report (BI report 5, ROO2-285, direction A of the canvas of 2026-10-23: the Rate Plans order).

// --- the Export menu of a card (ButtonLinkList toolbar, design system v1.27.0, as the Segment evolution card of ROO2-280:
// a head with the format, the scope and the period, an icon and a short line per file) -------------------------------------
type ExportProps = { scopeName: string, period: MonthRange, ready: boolean, exporting: boolean, onExport: (file: 'CURRENT_VIEW' | 'ALL_DATA') => void, rowWord: string };

export const ChannelExportMenu: React.FunctionComponent<ExportProps> = ({ scopeName, period, ready, exporting, onExport, rowWord }) =>
	<ButtonLinkList toolbar icon={<ArrowDownTrayIcon className="h-4 w-4" />} disabled={!ready || exporting}
		description={`Excel · ${scopeName} · ${periodLabel(period).replace(/ /g, ' ')}`}
		items={[
			{ name: 'Current view', detail: `This ${rowWord} as it shows now`, icon: <TableCellsIcon className="h-4 w-4" />, onClick: () => onExport('CURRENT_VIEW') },
			{ name: 'All data', detail: 'Each stay night, property and raw channel name', icon: <CircleStackIcon className="h-4 w-4" />, onClick: () => onExport('ALL_DATA') },
		]}>
		{exporting ? 'Preparing…' : 'Export'}
	</ButtonLinkList>;

// --- the mix chart (RQ-17 … RQ-19) -----------------------------------------------------------------------------------------
type MixProps = {
	tree: ChannelTree | undefined, order: string[], period: MonthRange, measure: MixMeasure, onMeasure: (m: MixMeasure) => void,
	comparison: MixComparison, onComparison: (c: MixComparison) => void, scope: RevenueScope, vat: VatBasis, state: ChartCardState,
	onRetry: () => void, error: string | null, lastYearNote: string | null,
	// RQ-19: a click on a bar highlights its row in the table.
	onPick: (rowId: string) => void,
	exportMenu: React.ReactNode,
};

const MIX_FORMAT = { rev: 'percent', rn: 'percent', adr: 'money' } as const;

export const ChannelMixCard: React.FunctionComponent<MixProps> = ({
	tree, order, period, measure, onMeasure, comparison, onComparison, scope, vat, state, onRetry, error, lastYearNote, onPick, exportMenu,
}) => {
	const rows = useMemo(() => tree === undefined ? [] : order.map(id => tree.rows.find(r => r.id === id)).filter((r): r is ChannelNode => r !== undefined),
		[tree, order]);
	const mix = useMemo(() => tree === undefined ? null : mixOf(rows, tree.total, measure, comparison, scope, vat), [rows, tree, measure, comparison, scope, vat]);
	const empty = tree !== undefined && tree.rows.length === 0;
	const what = measure === 'rev' ? `share of the ${revenueName(scope, vat)}` : (measure === 'rn' ? 'share of the room nights' : `ADR (${revenueName(scope, vat)})`);
	return <ChartCard title={`Channel mix · ${periodLabel(period)}`} period={`This period against ${comparison === 'stly' ? 'STLY' : 'LY actual'} · ${what}`}
		state={empty ? 'empty' : state} onRetry={onRetry} emptyLabel="Nothing on the books for this period, this year or last."
		errorLabel={error === null ? 'Could not load.' : `Could not load: ${error}`}
		actions={exportMenu}
		table={mix === null ? undefined : <DataTable {...barChartTable({ categories: mix.categories, series: mix.series, categoryLabel: 'Channel', format: MIX_FORMAT[measure], decimals: measure === 'adr' ? 2 : 1 })}
			framed={false} ariaLabel="Channel mix" />}
		caption={<>The first 15 channels of the table, in its order; Other = the rest added together. ADR shows as a value: ADRs do not add up.
			Click a bar to highlight its row in the table.{lastYearNote === null ? '' : ` ${lastYearNote}`}</>}>
		{/* The switches of the chart sit over it, not in the header: with Export and Chart | Table, four controls do not fit
			the header of a phone (their selects were cut at 400 px). */}
		<div className="mb-3 flex flex-wrap items-center gap-2">
			<ButtonTabs label="Measure of the mix" value={measure} onChange={v => onMeasure(v as MixMeasure)}
				options={[{ value: 'rev', label: 'Share of revenue' }, { value: 'rn', label: 'Share of RN' }, { value: 'adr', label: 'ADR' }]} />
			<ButtonTabs label="Comparison of the mix" value={comparison} onChange={v => onComparison(v as MixComparison)}
				options={[{ value: 'stly', label: 'STLY' }, { value: 'ly', label: 'LY actual' }]} />
		</div>
		{mix !== null && <BarChart categories={mix.categories} series={mix.series} format={MIX_FORMAT[measure]} decimals={measure === 'adr' ? 2 : 1}
			ariaLabel="Channel mix" tooltipLines={mix.tooltipLines} onBarClick={i => { const id = mix.rowIds[i]; if (id !== undefined) onPick(id); }} />}
	</ChartCard>;
};

// --- the table (RQ-06 … RQ-12) -------------------------------------------------------------------------------------------
type TableProps = {
	tree: ChannelTree | undefined, period: MonthRange, blocks: ChannelBlock[], onToggle: (block: ChannelBlock) => void,
	scope: RevenueScope, vat: VatBasis, state: ChartCardState, onRetry: () => void, resetKey: string, lastYearNote: string | null, lfl: boolean,
	error: string | null, exportMenu: React.ReactNode,
	buildUp?: { block: string, label: string, of: (node: ChannelNode) => (number | null)[] | null, total: (number | null)[] | null },
	onSelect?: (row: DataTableRow, level: number) => void, selectedId?: string,
	onSortChange?: (sort: DataTableSort | undefined, topRowIds: string[]) => void,
	// ROO2-284: a click on a clickable number (RN or REV of a cut): the row and the column id.
	onCellClick?: (row: DataTableRow, columnId: string) => void,
	// ROO2-315 part c2 (Q4): the ADR and the report rules of the scope, once (adrRulesNote); null without rules.
	rulesNote?: string | null,
	// ROO2-302 (Q1 C): the note that the held rooms of group blocks are not in this report yet (heldBlocksNote); null without them.
	heldNote?: string | null,
};

export const ChannelTableCard: React.FunctionComponent<TableProps> = ({
	tree, period, blocks, onToggle, scope, vat, state, onRetry, resetKey, lastYearNote, lfl, error, exportMenu, buildUp, onSelect, selectedId, onSortChange, onCellClick,
	rulesNote = null, heldNote = null,
}) => {
	const { columns, groups } = useMemo(() => columnsOf(blocks, buildUp ?? null, lfl), [blocks, buildUp, lfl]);
	const rows = useMemo(() => tree === undefined ? [] : tableRows(tree, scope, vat, buildUp?.of), [tree, scope, vat, buildUp]);
	const empty = tree !== undefined && tree.rows.length === 0;
	const missing = tree === undefined ? null : missingNote(missingOf([tree.total.cuts[0], tree.total.stly, tree.total.ly], scope, vat), '–', 'the period');
	return <ChartCard title={`Channels · ${periodLabel(period)}`} period={`Stay dates ${nightsLabel(period)} · past nights actual, later nights on the books`}
		info={{ title: 'Channels', content: <>The channel of a booking is its source in the PMS. Names that differ only in capitals or spaces are one channel.
			A Channel Group of the company puts several channels into one row; the row opens to its raw names.</> }}
		state={empty ? 'empty' : state} onRetry={onRetry} emptyLabel="Nothing on the books for this period, this year or last."
		errorLabel={error === null ? 'Could not load.' : `Could not load: ${error}`}
		actions={<>
			{exportMenu}
			<CheckboxMenu label={<><TableCellsIcon aria-hidden="true" className="h-4 w-4 text-gray-400" />Columns<span className="font-normal text-gray-500">· {blocks.length}</span></>}
				options={CHANNEL_BLOCKS} selected={blocks} onToggle={onToggle} anchor="bottom end" variant="button" />
		</>}
		caption={<>
			RN = sold room nights; REV = {revenueName(scope, vat)} ({revenueContent(scope)}). STLY = the same stay dates last year, as they stood one year ago
			today; LY actual = as they stand today. Δ% compares revenue. PU n = today − situation D-n; ADR Δ = ADR today − ADR at situation D-n. The ⓘ of a
			column gives its meaning. A row with ▸ opens to its raw channel names.{lastYearNote === null ? '' : ` ${lastYearNote}`}{missing === null ? '' : ` ${missing}`}
			{rulesNote === null ? null : <><br />{rulesNote}</>}
			{heldNote === null ? null : <><br />{heldNote}</>}
		</>}>
		{tree !== undefined && <DataTable key={resetKey} ariaLabel="Channels" framed={false} stickyHeader columns={columns} columnGroups={groups} rows={rows}
			defaultSort={{ columnId: 'rev', direction: 'desc' }} sortCycle="threeClicks" rowLimit={ROW_LIMITS}
			footer={tree.rows.length > 1 ? totalRow(tree, scope, vat, blocks.includes('build') ? (buildUp?.total ?? null) : null) : undefined} onRowSelect={onSelect} selectedRowId={selectedId} onSortChange={onSortChange}
			onCellClick={onCellClick} />}
	</ChartCard>;
};

// --- the build-up (RQ-13 … RQ-16) ---------------------------------------------------------------------------------------
type EvolutionProps = {
	evolution: ChannelEvolution | undefined, tree: ChannelTree | undefined, period: MonthRange, measure: EvolutionMeasure,
	onMeasure: (m: EvolutionMeasure) => void, window: number, onWindow: (w: number) => void, state: ChartCardState, error: string | null,
	onRetry: () => void, order: string[], focus: string | null, onFocus: (id: string | null) => void,
};

const FOCUS_FORMAT = { rn: 'number', rev: 'money', adr: 'money' } as const;
const lastDayOf = (yearMonth: string) => new Date(Date.UTC(Number(yearMonth.slice(0, 4)), Number(yearMonth.slice(5, 7)), 0)).toISOString().slice(0, 10);

export const ChannelEvolutionCard = React.forwardRef<HTMLDivElement, EvolutionProps>(({
	evolution, tree, period, measure, onMeasure, window, onWindow, state, error, onRetry, order, focus, onFocus,
}, ref) => {
	const data = useMemo(() => {
		if (evolution === undefined || tree === undefined) return null;
		const lastNight = lastDayOf(period.to);
		const cut = (values: (number | null)[]) => values.map((v, i) => evolution.days[i] > lastNight ? null : v);
		const { lines, other } = evolutionLines(evolution, tree, order);
		const all = [...lines, ...(other === null ? [] : [other])];
		let focused: ChannelLine | null = focus === null ? null : (all.find(l => l.id === focus) ?? evolutionByRow(evolution, tree).get(focus) ?? null);
		if (focused !== null && focused.id === OTHER_LINE && other === null) focused = null;
		const series = focused === null ? all.map(l => ({ id: l.id, label: l.title, values: cut(pointsOf(l.thisYear, measure)),
			...(l.id === OTHER_LINE ? { role: 'other' as const, dashed: true } : {}) }))
			: [{ id: 'thisYear', label: `${focused.title} · this year`, role: 'own' as const, values: cut(pointsOf(focused.thisYear, measure)) },
				{ id: 'lastYear', label: 'Last year (same lead time)', role: 'compset' as const, dashed: true, values: pointsOf(focused.lastYear, measure) }];
		return { series, days: evolution.days, focused };
	}, [evolution, tree, period, measure, order, focus]);
	const finished = data !== null && lastDayOf(period.to) < data.days[0];
	const nothing = data !== null && data.series.every(s => s.values.every(v => v === null || v === 0));
	const format = measure === 'rn' ? 'number' : 'money';
	const decimals = measure === 'adr' ? 2 : 0;
	const name = data?.focused?.title ?? null;
	const f = data?.focused === null || data?.focused === undefined ? null : (() => {
		const days = data.days.filter(d => d <= lastDayOf(period.to)).length;
		return focusFigures(pointsOf(data.focused.thisYear, measure).slice(0, days), pointsOf(data.focused.lastYear, measure).slice(0, days), measure);
	})();
	return <div ref={ref} className="scroll-mt-4"><ChartCard title={`${name !== null ? `How ${name} built up` : 'How each channel built up'} · ${periodLabel(period)}`}
		period={data === null ? 'As it stood on each day of the window' : `As it stood on each day ${formatDate(data.days[0])} – ${formatDate(data.days[data.days.length - 1])}`}
		state={finished || nothing ? 'empty' : state} onRetry={onRetry} errorLabel={error === null ? 'Could not load.' : `Could not load: ${error}`}
		emptyLabel={finished ? 'This stay period had finished before the window starts. Widen the window.' : 'Nothing on the books in this window.'}

		table={data === null ? undefined : <DataTable {...lineChartTable({ dates: data.days, series: data.series, format, decimals })}
			framed={false} size="normal" ariaLabel={name === null ? 'How each channel built up' : `How ${name} built up`} />}
		caption={<>{focus !== null ? 'Last year = the same stay dates one year earlier, as they stood on the same day one year earlier. Select the name again or "All channels" to see all lines.'
			: 'One line for each of the first 6 channels of the table, in its order; Other = the rest added together (dashed). Click a channel in the table or the legend to compare it with last year.'}</>}>
		{/* As the mix card: the switches over the chart, so that the header keeps only Chart | Table on a phone. */}
		<div className="mb-3 flex flex-wrap items-center gap-2">
			{focus !== null && <ToolbarButton icon={<ArrowLeftIcon className="h-4 w-4" />} onClick={() => onFocus(null)}>All channels</ToolbarButton>}
			<ButtonTabs label="Measure of the chart" value={measure} onChange={v => onMeasure(v as EvolutionMeasure)}
				options={[{ value: 'rn', label: 'RN' }, { value: 'rev', label: 'Revenue' }, { value: 'adr', label: 'ADR' }]} />
			<ButtonTabs label="Window of the chart" value={String(window)} onChange={v => onWindow(Number(v))}
				options={WINDOWS.map(w => ({ value: String(w), label: `${w} days` }))} />
		</div>
		{f !== null && <div className="mb-4"><KpiStrip ariaLabel={`How ${name ?? ''} built up: figures`} items={[
			{ id: 'now', label: 'Now', value: f.now, format: FOCUS_FORMAT[measure], decimals,
				change: f.change === null ? undefined : { value: f.change, format: 'percent', label: 'vs last year at this point', upIsGood: null } },
			{ id: 'lastYear', label: 'LY at this point', value: f.lastYear, format: FOCUS_FORMAT[measure], decimals },
			...(measure === 'adr' ? [] : [
				{ id: 'week', label: 'Last 7 days', value: f.week, format: FOCUS_FORMAT[measure] },
				{ id: 'weekLastYear', label: 'Last 7 days, LY', value: f.weekLastYear, format: FOCUS_FORMAT[measure] },
			]),
		]} /></div>}
		{data !== null && <LineChart dates={data.days} series={data.series} format={format} decimals={decimals} tooltipPrefix="As of" tooltipOrder="value"
			ariaLabel={name === null ? 'How each channel built up' : `How ${name} built up`} onSeriesClick={id => { if (focus === null) onFocus(id); }} />}
	</ChartCard></div>;
});
ChannelEvolutionCard.displayName = 'ChannelEvolutionCard';

// M-12 for the BUILD-UP column: the line of a row over the window, in RN (also when the chart shows ADR) or in revenue; the
// days after the last night of the period have no value.
export const channelBuildUp = (evolution: ChannelEvolution | undefined, tree: ChannelTree | undefined, measure: EvolutionMeasure, period: MonthRange) => {
	if (evolution === undefined || tree === undefined) return null;
	const m: EvolutionMeasure = measure === 'adr' ? 'rn' : measure;
	const byRow = evolutionByRow(evolution, tree);
	const lastNight = lastDayOf(period.to);
	const cut = (values: (number | null)[]) => values.map((v, i) => evolution.days[i] > lastNight ? null : v);
	return {
		label: m === 'rn' ? 'RN' : 'REV',
		of: (node: ChannelNode) => {
			const row = byRow.get(node.id);
			return cut(row === undefined ? evolution.days.map(() => 0) : pointsOf(row.thisYear, m));
		},
		total: (() => { const t = byRow.get('total'); return t === undefined ? null : cut(pointsOf(t.thisYear, m)); })(),
	};
};
